import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getDomainString } from '../../../apps/commerce/checkout/helpers';
import TabbedCheckout from '../../../apps/commerce/checkout/TabbedCheckout';
import makePayment from '../../../apps/commerce/checkout/payment_providers/landlord_docs_payment';
import Modal from '../../../components/Modal';
import '../../../../sass/components/forms.scss';
import '../../../../sass/components/landlord_docs_checkout.scss';

const LandlordDocsCheckout = ({
  amount,
  baseUrl,
  buttonText,
  csEmail,
  context,
  currency,
  currencySymbol,
  loginLink,
  modalTitle,
  phoneNumber,
  platform,
  productId,
  productName,
  quantity,
  siteId,
  staticFileServer,
  subDirectory,
  trackingCategory,
  trackingDesc,
  userId,
  email,
  localisations
}) => {
  const [emailValue, updateEmailValue] = useState('');
  const [emailConfirmValue, updateEmailConfirmValue] = useState('');
  const [emailCaptureView, setEmailCaptureView] = useState(false);
  const emailInput = useRef(null);
  const emailConfirmInput = useRef(null);
  const amountErrorMessage = '';
  const [emailErrorMessage, updateEmailErrorMessage] = useState('');
  const [confirmErrorMessage, updateConfirmErrorMessage] = useState('');
  const [showDialog, updateShowDialog] = useState(false);
  const randomId = Math.random()
    .toString(36)
    .substr(2, 5);

  const open = () => {
    return updateShowDialog(true);
  };

  const close = () => {
    setInitialCaptureView();
    return updateShowDialog(false);
  };

  const setInitialCaptureView = useCallback(() => {
    if (!(userId || email)) {
      return setEmailCaptureView(true);
    }
    return setEmailCaptureView(false);
  }, [userId, email]);

  useEffect(() => {
    setInitialCaptureView();
  }, [setInitialCaptureView]);

  const proceedToPayment = (event) => {
    if (!validateEmail(emailValue)) {
      updateEmailErrorMessage('Please provide a valid email');
      return emailFocus();
    }
    if (!validateEmail(emailConfirmValue)) {
      updateConfirmErrorMessage('Please confirm your email');
      return emailConfirmFocus();
    }
    if (emailValue !== emailConfirmValue) {
      return emailConfirmFocus();
    }
    email = emailValue;
    return setEmailCaptureView(false);
  };

  const emailFocus = () => {
    return emailInput.current.focus();
  };

  const emailConfirmFocus = () => {
    return emailConfirmInput.current.focus();
  };

  const validateEmail = (str) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(str);
  };

  const beforePaymentCheck = (provider) => {
    let errorMessage = '';
    if (!amount) {
      errorMessage = `Amount can't be empty.`;
    }
    return errorMessage;
  };

  const getUserData = () => {
    if (userId) {
      return {
        id: userId,
      };
    }
    if (email) {
      return {
        email: email,
      };
    }
    if (emailValue) {
      return {
        email: emailValue,
      };
    }
    return {};
  };

  function paymentSuccessCallback({ id }) {
    // Additional callback if needed
  }

  const paymentSuccess = (provider, token) => {
    const products = [
      { id: productId.toString(), quantity: parseInt(quantity, 10) },
    ];
    const user = getUserData();
    const payload = {
      amount: parseInt((parseFloat(amount) * 100).toFixed(0), 10),
      baseUrl,
      currency: currency.toLowerCase(),
      customerServiceContactDetails: {
        phoneNumber,
        email: csEmail,
      },
      domain: getDomainString(siteId),
      paymentProvider: provider,
      platform,
      products,
      successCallback: paymentSuccessCallback,
      successUrl: `/${subDirectory}/myorders.pl?mode=Agreements&M_product_id=${productName}&prod_category=${trackingCategory}&desc=${trackingDesc}&email=${email ||
        emailValue}&a=${amount}`,
      token: token,
      user,
    };
    return makePayment(payload);
  };

  return (
    <>
      <p className="sr-only" id={`checkout-button-desc-${randomId}`}>
        Proceed to payment for {trackingDesc} for £{amount}
      </p>
      <button
        className="order_submit_button button"
        onClick={open}
        aria-haspopup="true"
        aria-expanded={showDialog}
        aria-describedby={`checkout-button-desc-${randomId}`}
      >
        {buttonText}
      </button>
      {showDialog ? (
        <Modal
          close={close}
          showDialog={showDialog}
          modalLabelText={modalTitle}
          id={randomId}
        >
          {emailCaptureView ? (
            <div className="email-capture-view">
              <div className="email-capture-view__inner">
                <div className="email-capture-view__input">
                  <label className="label" htmlFor={`email-${randomId}`}>
                    Enter email address for PDF
                  </label>
                  <input
                    className="input"
                    type="email"
                    name="email"
                    id={`email-${randomId}`}
                    placeholder=""
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={emailErrorMessage ? 'true' : 'false'}
                    onChange={(event) => updateEmailValue(event.target.value)}
                    ref={emailInput}
                    autoFocus
                  />
                  {emailErrorMessage ? (
                    <span className="form_validation" role="alert">
                      {emailErrorMessage}
                    </span>
                  ) : null}
                </div>
                <div className="email-capture-view__input">
                  <label className="label" htmlFor={`confirm-${randomId}`}>
                    Confirm email address
                  </label>
                  <input
                    className="input"
                    type="email"
                    name="confirm"
                    id={`confirm-${randomId}`}
                    placeholder=""
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={confirmErrorMessage ? 'true' : 'false'}
                    ref={emailConfirmInput}
                    onChange={(event) =>
                      updateEmailConfirmValue(event.target.value)
                    }
                  />
                  {confirmErrorMessage ? (
                    <span className="form_validation" role="alert">
                      {confirmErrorMessage}
                    </span>
                  ) : null}
                </div>
              </div>
              {loginLink ? (
                <p className="login_message">
                  Aready registered with SpareRoom?{' '}
                  <a href={loginLink}>Log in</a>
                </p>
              ) : null}
              <button
                className="button email_submit_button"
                onClick={proceedToPayment}
              >
                Continue to payment
              </button>
            </div>
          ) : (
            <TabbedCheckout
              staticFileServer={staticFileServer}
              paymentSuccess={paymentSuccess}
              amount={amount}
              currencySymbol={currencySymbol}
              beforeSubmitErrorMessage={beforePaymentCheck}
              payPalErrorMessage={amountErrorMessage}
              stripeButtonText={`${localisations.pay} ${currencySymbol}${amount}`}
              localisations={localisations}
            />
          )}
        </Modal>
      ) : null}
    </>
  );
};

export default LandlordDocsCheckout;
